// @givmo/compliance — the review's view model (CCM-R1-REVIEW-WEB; plan §1.8; UX slice 4).
//
// From one presented revision's reads to the page's sections, in the backend's order: filings on
// the calendar that read otherwise, filings identified, questions, conditions the rules don't settle
// and no filing; then what Givmo doesn't evaluate. Nothing here decides anything: every result is
// the server's, and this only arranges and words it.
//
// ══ THE GROUPINGS (the design stop's D8, as approved) ══════════════════════════════════════════
//
//   • The questions one recorded field answers are asked once: every "solicits in X" is the one
//     "Where does … ask for contributions?" card.
//   • An organization answer's questions are asked together, per state, each in the state's own
//     words (N21; G2's served `source_words`).
//   • One unsettled condition across states is one card naming every result it leaves unresolved.
//   • Results whose conditions aren't met fold behind a count (N14).
//   • A filing on the calendar that reads otherwise is its change card alone, never also its item.
//
// ══ WORDS ══════════════════════════════════════════════════════════════════════════════════════
//
// Approved web words where D-CCM-51 gives them (`review-words.ts`), and the backend's own served
// words for anything they don't cover: a condition's `words`, a question of a kind no line words.
// Never an invented line, and never a hidden condition: the review shows its work (plan §3 row 3).

import {
  US_JURISDICTIONS,
  displayComplianceDate,
  displayLocalDate,
  localDayOf,
  type RuleProvenance,
} from './compliance-decode';
import type { WorkedOutDate } from './compliance-filings-decode';
import { datePassedWhileOpen } from './compliance-filings-model';
import {
  DATE_PASSED,
  extensionLine,
  firstPeriodLine,
  fiscalYearEndedLine,
  libraryBasisLine,
  longComplianceDate,
  monthDayWords,
} from './compliance-panel-model';
import {
  SOMEONE_IN_YOUR_ORGANIZATION,
  SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE,
} from './compliance-signing-model';
import { andList } from './compliance-submission-model';
import {
  addressKey,
  ruleKey,
  type ConditionAddress,
  type FactReading,
  type ItemDecision,
  type ResultCondition,
  type ReviewItem,
  type ReviewQuestion,
  type ReviewResult,
  type ReviewRevision,
  type RuleRef,
} from './review-decode';
import * as W from './review-words';
import type { FormFeeChart } from './compliance-preparation-decode';

// ── Names ──────────────────────────────────────────────────────────────────────────────────────

const JURISDICTION_NAMES = new Map(US_JURISDICTIONS.map((entry) => [entry.code, entry.name]));

/** What the page knows of a rule's name, from its result (G1's served presentation). */
export interface RuleName {
  jurisdictionName: string;
  displayTitle?: string;
  /**
   * The rule's served citation, its name where it has no display title (cut 1008-92, F2; APPROVED,
   * D-CCM-108, REVIEW-47's choice 2).
   */
  displayCitation?: string;
}

export type RuleNames = ReadonlyMap<string, RuleName>;

/** Every result's rule, by key. */
export function ruleNamesOf(results: readonly ReviewResult[]): RuleNames {
  return new Map(
    results.map((result) => [
      ruleKey(result.rule),
      {
        jurisdictionName: result.rule.jurisdictionName,
        displayTitle: result.rule.displayTitle,
        displayCitation: result.rule.provenance.ruleDisplayCitation,
      },
    ]),
  );
}

export function jurisdictionNameOf(code: string, names: RuleNames): string {
  for (const [key, name] of names) {
    if (key.startsWith(`${code}:`) && name.jurisdictionName.length > 0)
      return name.jurisdictionName;
  }
  return JURISDICTION_NAMES.get(code) ?? code;
}

/** "Maryland · Registration statement (COR-92)", as every Compliance screen names a filing. */
export function filingTitle(ref: RuleRef, names: RuleNames): string {
  const name = names.get(ruleKey(ref));
  const jurisdiction = name?.jurisdictionName || jurisdictionNameOf(ref.jurisdictionCode, names);
  const named = name?.displayTitle ?? name?.displayCitation;
  return named !== undefined ? `${jurisdiction} · ${named}` : jurisdiction;
}

/** A rule the library offers as a filing: one with a display title (G1). */
function isFiling(ref: RuleRef, names: RuleNames): boolean {
  return names.get(ruleKey(ref))?.displayTitle !== undefined;
}

// ── Values and periods, in words ───────────────────────────────────────────────────────────────

/** "$25,000" from exact decimal text; cents only where the figure has them. */
export function moneyWords(amount: string | undefined): string | undefined {
  if (amount === undefined || !/^-?\d+(\.\d+)?$/.test(amount.trim())) return undefined;
  const [whole = '0', fraction = ''] = amount.trim().replace(/^-/, '').split('.');
  const grouped = whole.replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const cents = fraction.replace(/0+$/, '');
  const sign = amount.trim().startsWith('-') ? '-' : '';
  return `${sign}$${grouped}${cents.length > 0 ? `.${fraction.padEnd(2, '0').slice(0, 2)}` : ''}`;
}

/** "the fiscal year ended Dec 31, 2025" from a served fiscal period; nothing for any other period. */
export function fiscalPeriodWords(canonical: string | undefined): string | undefined {
  const match = /^fiscal:\d{4}-\d{2}-\d{2}\/(\d{4}-\d{2}-\d{2})$/.exec(canonical ?? '');
  return match === null ? undefined : W.fiscalYearEnded(displayComplianceDate(match[1]));
}

/**
 * The last day of a served fiscal period ("Dec 31, 2026") where that year isn't over on `asOf`, the
 * revision's day: the current fiscal year, served whole while its figure is counted so far (cut
 * 1008-122; a year is current on its own last day, as the backend's `current_fiscal_year` reads it).
 * Nothing for a year that has ended, for any other period, or with no day to read it on: such a year
 * reads `fiscalPeriodWords`, whose "ended" would misstate one not yet over (cut 1008-125).
 */
export function soFarYearEnd(
  canonical: string | undefined,
  asOf: string | undefined,
): string | undefined {
  const end = /^fiscal:\d{4}-\d{2}-\d{2}\/(\d{4}-\d{2}-\d{2})$/.exec(canonical ?? '')?.[1];
  const day = asOf?.slice(0, 10);
  if (end === undefined || day === undefined || day.length === 0) return undefined;
  return day <= end ? displayComplianceDate(end) : undefined;
}

/**
 * The day a figure counted so far is counted through ("Sep 15, 2026"), off the reading of the
 * condition's own year (its source's day, C1), as the backend's W6 names it (review-218's fold).
 */
function countedThroughOf(
  condition: ResultCondition,
  inputs: readonly FactReading[] | undefined,
): string | undefined {
  const figure = inputs?.find(
    (reading) =>
      reading.factKey === 'monetary_figure' &&
      reading.dimensions.period === condition.measuredPeriod &&
      condition.inputRefs.includes(reading.requirement),
  );
  return figure?.sourceDate !== undefined ? displayComplianceDate(figure.sourceDate) : undefined;
}

/** The profile's served labels for the facts the review names by them (`PROFILE_PARAMETER_SPECS`). */
const PROFILE_LABELS: Readonly<Record<string, string>> = {
  fiscal_year_end: 'Fiscal year end',
  solicits_nationwide_or_online: 'Solicits nationwide or online',
  engages_paid_fundraiser: 'Engages a paid fundraiser, solicitor or fundraising consultant',
};

// ── Conditions, in words (N9, N49) ─────────────────────────────────────────────────────────────

/**
 * One condition, in the approved words where they cover it (N49: the personnel prong the frames
 * show, and a dollar line "at or below" an amount of contributions from the public over a fiscal
 * year, cut 1008-20's W2 over one not yet over on `asOf`, through the day its figure among `inputs` is
 * counted through), a state named for its code, the online answer by the profile's own served label,
 * an organization answer's quoted question, a question in Givmo's own words by its name, unquoted;
 * otherwise the condition's served words.
 */
export function conditionWords(
  condition: ResultCondition,
  names: RuleNames,
  asOf?: string,
  inputs?: readonly FactReading[],
): string {
  if (condition.personnelKind === 'no_compensated_solicitor') {
    return W.DOES_NOT_EMPLOY_A_PROFESSIONAL_SOLICITOR;
  }
  if (
    condition.measure === 'contributions_from_the_public' &&
    condition.sourceScope === 'from_the_public' &&
    condition.excludes.length === 0 &&
    condition.comparator === 'at_or_below'
  ) {
    const amount = moneyWords(condition.amountUsd);
    const soFar = soFarYearEnd(condition.measuredPeriod, asOf);
    if (amount !== undefined && soFar !== undefined) {
      return W.thresholdSoFarWords(
        W.CONTRIBUTIONS_FROM_THE_PUBLIC,
        amount,
        soFar,
        countedThroughOf(condition, inputs),
      );
    }
    const period = fiscalPeriodWords(condition.measuredPeriod);
    if (amount !== undefined && period !== undefined) {
      return W.thresholdWords(W.CONTRIBUTIONS_FROM_THE_PUBLIC, amount, period);
    }
  }
  if (condition.kind === 'solicitation_activity' || condition.kind === 'solicitation_outside') {
    return condition.words.replace(/\b([A-Z]{2})$/, (code) => jurisdictionNameOf(code, names));
  }
  if (condition.kind === 'online_presence') {
    // The record composes this leaf for the record ("…, which reaches MD only if an online presence
    // alone is soliciting there"); the screen names it by the fact it reads, as the facts list does.
    return PROFILE_LABELS.solicits_nationwide_or_online ?? condition.words;
  }
  if (condition.moduleWords !== undefined) {
    // Cut 1008-122 (W5): a question Givmo asks in its own words has no source to quote. Its served
    // words are Givmo's name for it (W5-r), shown as Givmo's, whatever its answer reads.
    return condition.words;
  }
  if (condition.kind === 'organization_answer') {
    // X2 (cut 1008-13's review): a retyped prong's words are its source's, verbatim (the catalog's
    // law), so they are quoted wherever a condition shows them.
    return `“${condition.words}”`;
  }
  const located = locatedJurisdiction(condition.inputRefs);
  if (condition.kind === 'fact_test' && located !== undefined) {
    // The gate on § 6-401(a)(3): named as its fact is (W2), not by the source's fragment.
    return W.locatedIn(jurisdictionNameOf(located, names));
  }
  if (
    condition.kind === 'fact_test' &&
    (condition.sourceWords !== undefined ||
      condition.inputRefs.some((ref) => ref.startsWith('organization_answer:')))
  ) {
    return `“${condition.sourceWords ?? condition.words}”`;
  }
  return condition.words;
}

/** The jurisdiction a `located_in` requirement names, if any of these reads one. */
function locatedJurisdiction(refs: readonly string[]): string | undefined {
  for (const ref of refs) {
    const match = /^located_in:jurisdiction=([A-Z]{2})$/.exec(ref);
    if (match !== null) return match[1];
  }
  return undefined;
}

// ── Facts, in words (N8, N54) ──────────────────────────────────────────────────────────────────

/** The profile's own field each fact is answered at; a fact with none has no door yet (G3). */
const ANSWERED_AT: Readonly<Record<string, string>> = {
  fiscal_year_end: 'fiscal_year_end',
  solicitation_activity: 'solicitation_jurisdictions',
  // Asking in a state other than one is read from the same list (D-CCM-114).
  solicitation_outside: 'solicitation_jurisdictions',
  solicits_nationwide_or_online: 'solicits_nationwide_or_online',
  engages_paid_fundraiser: 'engages_paid_fundraiser',
};

export interface FactLine {
  key: string;
  label: string;
  value: string;
  by?: string;
  on?: string;
  /** The profile field "Correct" opens; absent where no door records the fact yet (G3). */
  correctAt?: string;
  /** Recorded after the decision this card quotes (JQ1): the fact that moved. */
  changed?: boolean;
  /** Where Givmo read a fact of its own records (W2, W3), in place of who recorded it and when. */
  source?: string;
}

/** W2-a, W2-b and W3: which of Givmo's records a fact was read from; none for anything else. */
function recordSource(reading: FactReading, orgName: string): string | undefined {
  if (reading.sourceKind !== 'irs_record') return undefined;
  switch (reading.method) {
    case 'irs_master_file_address':
      return W.fromTheIrsAddress(orgName);
    case 'charity_record_address':
      return W.fromGivmosRecord(orgName);
    case 'master_file_listing':
    case 'auto_revocation_list':
      return W.FROM_THE_IRS_LISTS;
    default:
      return undefined;
  }
}

function yesNo(value: unknown): string {
  return value === true ? W.YES : value === false ? W.NO : '—';
}

function factLabelAndValue(
  reading: FactReading,
  result: ReviewResult,
  names: RuleNames,
  asOf: string,
): { label: string; value: string } {
  switch (reading.factKey) {
    case 'solicitation_activity':
      return {
        label: `Solicits contributions in ${jurisdictionNameOf(reading.dimensions.jurisdiction ?? '', names)}`,
        value: yesNo(reading.value),
      };
    case 'solicits_nationwide_or_online':
    case 'engages_paid_fundraiser':
      return {
        label: PROFILE_LABELS[reading.factKey] ?? reading.factKey,
        value: yesNo(reading.value),
      };
    case 'fiscal_year_end':
      return {
        label: PROFILE_LABELS.fiscal_year_end ?? 'Fiscal year end',
        value: monthDayWords(typeof reading.value === 'string' ? reading.value : undefined) ?? '—',
      };
    case 'monetary_figure': {
      // Cut 1008-20's W3 for a year not yet over on the revision's day, through the day the figure is
      // counted through (its source's day); else N54's year ended.
      const soFar = soFarYearEnd(reading.dimensions.period, asOf);
      const period =
        soFar !== undefined
          ? W.soFarInTheFiscalYearEnding(
              soFar,
              reading.sourceDate !== undefined
                ? displayComplianceDate(reading.sourceDate)
                : undefined,
            )
          : fiscalPeriodWords(reading.dimensions.period);
      if (reading.dimensions.measure === 'contributions_from_the_public' && period !== undefined) {
        return {
          label: W.contributionsFactLabel(period),
          value: moneyWords(typeof reading.value === 'string' ? reading.value : undefined) ?? '—',
        };
      }
      break;
    }
    case 'located_in':
      return {
        label: W.locatedIn(jurisdictionNameOf(reading.dimensions.jurisdiction ?? '', names)),
        value: yesNo(reading.value),
      };
    case 'federal_tax_exemption_in_effect':
      return { label: W.FEDERAL_TAX_EXEMPTION_IN_EFFECT, value: yesNo(reading.value) };
    case 'solicitation_outside':
      // O1 (APPROVED, D-CCM-117): the state by its name, as the states list's fact line names it.
      return {
        label: W.solicitsInAnotherState(
          jurisdictionNameOf(reading.dimensions.jurisdiction ?? '', names),
        ),
        value: yesNo(reading.value),
      };
    case 'organization_answer': {
      // CCM-R1-FACTS-W5-NAMED: a question Givmo asks in its own words is named as the record names
      // it (W5-r), never by a line that only reads its answer (the dollar line reads W5's).
      if (reading.moduleName !== undefined) {
        return { label: reading.moduleName, value: yesNo(reading.value) };
      }
      const leaf = result.conditions.find((condition) =>
        condition.inputRefs.includes(reading.requirement),
      );
      // Quotation marks are a source's: only a fact test's words are its source's question. A
      // prong the answer decides (a personnel prong) keeps its condition's words, unquoted (N49).
      if (leaf !== undefined) {
        return {
          label:
            leaf.kind === 'fact_test'
              ? `“${leaf.words}”`
              : conditionWords(leaf, names, asOf, result.inputsUsed),
          value: yesNo(reading.value),
        };
      }
      break;
    }
    case 'register_record': {
      // RF1 (D-CCM-112): the register's own record of a filing, by its state's name.
      const label = W.registerRecordFactLabel(
        reading.dimensions.record,
        reading.dimensions.obligation_class,
        jurisdictionNameOf(reading.dimensions.jurisdiction ?? '', names),
      );
      if (label !== undefined) return { label, value: yesNo(reading.value) };
      break;
    }
    case 'register_period_record': {
      // RF-p (cut 1008-122's fact, PENDING): the register's record of one year's filing, by its
      // filing, its year and its state's name, never by the line that reads it.
      const year = fiscalPeriodWords(reading.dimensions.period);
      const label =
        year !== undefined
          ? W.registerPeriodRecordFactLabel(
              reading.dimensions.record,
              reading.dimensions.obligation_class,
              jurisdictionNameOf(reading.dimensions.jurisdiction ?? '', names),
              year,
            )
          : undefined;
      if (label !== undefined) return { label, value: yesNo(reading.value) };
      break;
    }
    default:
      break;
  }
  // No approved name: the words of the condition that reads it, as served.
  const leaf = result.conditions.find((condition) =>
    condition.inputRefs.includes(reading.requirement),
  );
  const value =
    typeof reading.value === 'boolean'
      ? yesNo(reading.value)
      : typeof reading.value === 'string'
        ? (moneyWords(reading.value) ?? reading.value)
        : String(reading.value ?? '—');
  return { label: leaf?.words ?? reading.factKey, value };
}

// ── The date block (the add sheet's approved card) ─────────────────────────────────────────────

export interface DateCard {
  lead: string;
  /** The year a first year's date is for, in the panel's approved year line. */
  yearLine?: string;
  /** F1 (D-CCM-112): how Givmo counted the first year, under its date (D-CCM-110 J-F). */
  firstPeriod?: string;
  /** N1 after the date, while the year is held open by its automatic extension (CCM-R1-INTAKE-MDEXT-OPEN). */
  leadNote?: string;
  dated: boolean;
  basis?: string;
  aside?: string;
  provenance: RuleProvenance;
}

/**
 * The previewed dates, as the add sheet's "Next due" card words them. `asOf` is the day the revision
 * worked them out: a date that had passed by then while its window, counting the automatic extension
 * (the served `windowClosesOn`), was still open carries N1 (CCM-R1-INTAKE-MDEXT-OPEN, D-CCM-56).
 */
export function previewCard(
  previewed: readonly WorkedOutDate[],
  result: ReviewResult,
  fiscalYearEnd: string | undefined,
  asOf?: string,
): DateCard {
  const jurisdictionName = result.rule.jurisdictionName;
  const provenance = result.rule.provenance;
  const dated = previewed.find((entry) => entry.dueOn !== undefined);
  if (dated !== undefined) {
    // The first year a sending dates (the served `first_period`, D-CCM-110 J-F) says which year it
    // is and how Givmo counted it (F1); any other year's card is as it was.
    const isFirstYear = dated.firstPeriod?.first === true;
    const yearLine = isFirstYear ? fiscalYearEndedLine(dated) : undefined;
    return {
      lead: longComplianceDate(dated.dueOn),
      ...(asOf !== undefined && datePassedWhileOpen(dated, asOf) ? { leadNote: DATE_PASSED } : {}),
      ...(yearLine !== undefined ? { yearLine } : {}),
      ...(isFirstYear ? { firstPeriod: firstPeriodLine(jurisdictionName) } : {}),
      dated: true,
      basis: libraryBasisLine({
        dueDateBasis: dated.dueDateBasis,
        sourceDeadline: dated.sourceDeadline,
        fiscalYearEnd,
        jurisdictionName,
      }),
      aside: extensionLine(dated.extension),
      provenance,
    };
  }
  const first = previewed[0];
  if (first === undefined) return { lead: W.NO_DATE_YET, dated: false, provenance };
  if (first.dueDateBasis === 'before_first_solicitation') {
    return { lead: W.beforeFirstSolicit(jurisdictionName), dated: false, provenance };
  }
  return {
    lead: W.NOT_WORKED_OUT_YET,
    dated: false,
    basis:
      result.rule.anchor?.statement ??
      libraryBasisLine({
        dueDateBasis: first.dueDateBasis,
        sourceDeadline: first.sourceDeadline,
        fiscalYearEnd,
        jurisdictionName,
      }),
    provenance,
  };
}

// ── The cards ──────────────────────────────────────────────────────────────────────────────────

export type Mark = 'new' | 'changed';

/** N9: one condition and how it came out ("met", "not met", "not known"), nothing appended. */
export interface ConditionLine {
  words: string;
  outcome: string;
}

export interface Basis {
  conditions: ConditionLine[];
  evaluatedOn: string;
}

/** N24: the evidence a met result's source may ask for, beside the no-filing it causes (J1). */
export interface EvidenceLine {
  lead: string;
  quote: string;
  citation?: string;
}

export interface FilingCard {
  itemKey: string;
  ref: RuleRef;
  title: string;
  date: DateCard;
  facts: FactLine[];
  basis: Basis;
  /** W4 (MD-25K's G2): what the result's line leaves open, beside the result, in N24's note. */
  openDetail?: EvidenceLine;
  mark?: Mark;
  /** JQ1: an earlier "Not filing" whose evidence moved, quoted (N35). */
  reopened?: { decision: ItemDecision; cause: 'facts' | 'rule' };
  /** D-CCM-58 (U16): the filing is the organization's, its tracking stopped (S5). */
  stopped?: StoppedFiling;
}

/** A filing on the register whose tracking stopped: when, who stopped it, and how. */
export interface StoppedFiling {
  stoppedAt: string;
  stoppedByUserId?: string;
  /** `connector` when it was stopped through the person's connected AI (D-CCM-96). */
  stoppedChannel?: string;
}

export interface OnCalendarLine {
  itemKey: string;
  title: string;
  obligationId: string;
  /** Another person's addition since this screen opened (N34). */
  addedBy?: ItemDecision;
}

export interface ExclusionCard {
  itemKey: string;
  ref: RuleRef;
  title: string;
  label: string;
  /** W4 (MD-25K's G2): what the result's line leaves open, beside the result, in N24's note. */
  openDetail?: EvidenceLine;
  evidence?: EvidenceLine;
  facts: FactLine[];
  basis: Basis;
  mark?: Mark;
}

export type AnswerControl =
  | { kind: 'jurisdictions' }
  | { kind: 'month_day' }
  | { kind: 'boolean'; parameterKey: string }
  /** No door answers it yet (G3): the words, "I don't know" and an upload only. */
  | { kind: 'none' };

export interface QuestionCard {
  key: string;
  itemKeys: string[];
  title: string;
  /**
   * `stale`: a record of Givmo's own past its freshness rule (S), which nothing the organization
   * answers settles; `online`: a state's own question on the online leg (J-C).
   */
  kind?: 'stale' | 'online';
  /** The quoted words' served citation, on the cards that show one (W1's, the online question's). */
  citation?: string;
  /** The source's own example beside the question (E). */
  example?: { lead: string; quote: string; citation?: string };
  /** The fee to file what the answer decides, as its form prints it (F). */
  fee?: { caption: string; chart: FormFeeChart };
  /** An organization answer's questions, each in its source's own words (N21). */
  quoted: string[];
  /** The decisions card's name for it: the title, with an organization answer's first words. */
  listTitle: string;
  decides: string[];
  control: AnswerControl;
  /**
   * The card's one line under its decides line (a stale record's, under its title), and no card
   * draws a second: where a question no field here answers is answered (RQ2, D-CCM-112: the register's
   * record), why it is asked (W1-a), N21's lead over the online question's source words, or S.
   */
  door?: string;
  mark?: Mark;
  /** "I don't know", recorded earlier on this evidence (N22's "You don't know yet"). */
  standing?: ItemDecision;
  /**
   * The earliest next date among the filings it decides: its first item's `sort_on`, the backend's
   * "prioritized by upcoming work" (plan §1.7). Undated when none of them has a date yet.
   */
  sortOn?: string;
}

export interface IssueCard {
  key: string;
  itemKeys: string[];
  title: string;
  /** The module's own words for a question no source settles (served; choice 5), or none. */
  reasonWords: string[];
  /** A source's own words where it leaves the question open, quoted. */
  sourceWords: string[];
  /** Beside each of `sourceWords`, its source's citation where served (J3's two sources, cut 1008-122). */
  sourceCitations: (string | undefined)[];
  /**
   * W4 (MD-25K's G2), leading the source's words it would quote, where the card quotes them first: its
   * result's line leaves that open, and the card never quotes the same words twice.
   */
  openDetailLead?: string;
  blocks: string[];
  facts: FactLine[];
  mark?: Mark;
}

/**
 * A filing on the calendar that reads otherwise (CCM-R1-REVIEW-CHANGES; cut 1008-11's D1):
 * - `repin`: its rule was updated (frames 101, 102), so "Use the updated rule" or "Later";
 * - `no_filing`: it now reads as no filing (frame 105), so "Keep tracking it" or "Stop tracking it".
 * A change of moved dates alone, or to a reading that needs more facts, takes no card (the GO's Q2).
 */
export type ChangeKind = 'repin' | 'no_filing';

export interface ChangeCard {
  itemKey: string;
  title: string;
  kind: ChangeKind;
  /** N40's first line, for a filing that now reads as no filing. */
  line?: string;
  /** N36's day, where its rule was updated: the version's own, else the day it was flagged. */
  updatedOn?: string;
  evidence?: EvidenceLine;
  obligationId: string;
  mark?: Mark;
}

export interface DecidedCard {
  itemKey: string;
  kind: string;
  /** A change's own kind, which words what its acceptance did. */
  changeKind?: ChangeKind;
  ref?: RuleRef;
  title: string;
  decision: ItemDecision;
  /** "We file it" and the filing not on the calendar yet: "Add a filing" stays offered (JQ2). */
  offerAddFiling: boolean;
}

export interface ReviewView {
  changes: ChangeCard[];
  filings: FilingCard[];
  onCalendar: OnCalendarLine[];
  questions: QuestionCard[];
  issues: IssueCard[];
  noFiling: { shown: ExclusionCard[]; folded: ExclusionCard[] };
  decided: DecidedCard[];
  notSupported: { count: number; titles: string[]; inPlacesTold: string[] };
  evaluatedRuleCount: number;
  /** Whether anything evaluates for this organization (frame 106 when nothing does). */
  anythingEvaluates: boolean;
}

export interface ReviewModelInput {
  revision: ReviewRevision;
  items: readonly ReviewItem[];
  results: readonly ReviewResult[];
  /** The organization's name as the host shows it, for N17–N20. */
  orgName: string;
  personName: (userId: string) => string | undefined;
  /** The filings on the calendar (confirmed obligations), by id: each one's rule, and the day a rule
   * change flagged it. */
  calendar: ReadonlyMap<string, { ruleKey: string; ruleChangeFlaggedOn?: string }>;
  /** The fiscal year end on record, in words ("December 31"). */
  fiscalYearEnd?: string;
  /** The jurisdictions the organization records soliciting in (W14). */
  toldJurisdictions?: ReadonlySet<string>;
  /** Re-presentation: the items the person's screen showed before, and those whose evidence moved. */
  previousItemKeys?: ReadonlySet<string>;
  changedItemKeys?: ReadonlySet<string>;
  /** Decisions recorded by someone else since the person's screen opened (N34). */
  othersSince?: ReadonlySet<string>;
  /** D-CCM-58 (U16): the filings whose tracking stopped, by obligation id (off the calendar). */
  stopped?: ReadonlyMap<string, StoppedFiling>;
}

/** The fallback for a person the host can't name, where the phrase opens a sentence. */
export function nameOrSomeone(
  personName: (userId: string) => string | undefined,
  userId: string | undefined,
): string {
  return nameIfKnown(personName, userId) ?? SOMEONE_IN_YOUR_ORGANIZATION;
}

/**
 * The roster's name for a person, or undefined where it can't name them: for a line that puts the
 * person mid-sentence, whose word function says who in its own case (D-CCM-99).
 */
export function nameIfKnown(
  personName: (userId: string) => string | undefined,
  userId: string | undefined,
): string | undefined {
  return userId !== undefined ? personName(userId) : undefined;
}

/** A decision's day, in the frames' form ("Nov 12, 2026"), as the viewer's calendar reads the instant. */
export function decidedDay(decision: ItemDecision): string {
  return displayLocalDate(decision.decidedAt);
}

/**
 * Whether a standing decision rests on what this item shows now (JQ1 (A), D-CCM-37). A change's
 * "Keep tracking it" stands this way until the result, its facts or the rule move (cut 1008-11's
 * D2); its acceptance moves or stops the filing, so no later revision carries it.
 */
function standingOn(item: ReviewItem, revision: ReviewRevision): ItemDecision | undefined {
  const decision = item.decision;
  if (decision === undefined || decision.revisionId === revision.revisionId) return undefined;
  return decision.evidenceUnchanged ? decision : undefined;
}

/** A filing's or a no-filing's decision that settles it: accepted or denied. Anything else — a
 * deferral, or a decision this web doesn't know — leaves the card open, never a mislabelled line. */
function settles(decision: ItemDecision): boolean {
  return decision.decision === 'accept' || decision.decision === 'deny';
}

/**
 * O3 (D-CCM-58): a filing the review added and the organization then stopped comes back with its
 * earlier "Add to calendar" still recorded. Only a decision made since the stop settles it, as the
 * backend reads the same filing's ask (be#2811); one with no time recorded doesn't.
 */
function standsSinceStop(decision: ItemDecision, stoppedAt: string | undefined): boolean {
  if (stoppedAt === undefined) return true;
  return (
    decision.decidedAt !== undefined && Date.parse(decision.decidedAt) >= Date.parse(stoppedAt)
  );
}

/**
 * O3's twin (be#2811's `_decision_undone`): a "Stop tracking it" on a filing that is on the calendar
 * again was undone when the filing was started again from its own panel, so it settles nothing and
 * the change waits on a new choice. A change is only ever about a filing on the calendar, so a
 * standing one here was always undone that way.
 */
function undoneByRestart(
  kind: ChangeKind | undefined,
  decision: ItemDecision,
  tracked: boolean,
): boolean {
  return kind === 'no_filing' && decision.decision === 'accept' && tracked;
}

function reopenedOn(item: ReviewItem, revision: ReviewRevision): ItemDecision | undefined {
  const decision = item.decision;
  if (decision === undefined || decision.revisionId === revision.revisionId) return undefined;
  return decision.evidenceUnchanged ? undefined : decision;
}

/** A change item's card, by D1's table (cut 1008-11): which choices it takes, or none (Q2). */
function changeKindOf(item: ReviewItem): ChangeKind | undefined {
  const change = item.change;
  if (change === undefined) return undefined;
  if (change.result === 'rule_excluded') return 'no_filing';
  if (change.result === 'filing_identified' && change.changes.includes('rule_reversioned'))
    return 'repin';
  return undefined;
}

/**
 * N40's first line: another rule's met conditions (frame 105), or, for its own conditions no longer
 * met, its other form with N13's words as the reason (U4's render). For its own filing exception
 * met (`no_filing_required`), the line the no-filing card shows for it, D-CCM-19's label (that
 * placement approved by D-CCM-60): its choices never stand without a reason.
 */
function noFilingLine(result: ReviewResult, title: (ref: RuleRef) => string): string | undefined {
  const exclusion = result.exclusion;
  if (exclusion?.kind === 'lifted_by_rule' && exclusion.liftingRule !== undefined)
    return W.nowMeetsConditionsOf(title(exclusion.liftingRule));
  if (exclusion?.kind === 'conditions_not_met') {
    const line = decidedByRecordsAlone(result)
      ? W.RECORDS_DONT_MEET_CONDITIONS
      : W.CONDITIONS_NOT_MET;
    return W.nowReadsNoFiling(`${line.charAt(0).toLowerCase()}${line.slice(1)}`);
  }
  if (exclusion?.kind === 'no_filing_required') return W.CONDITIONS_MET_NO_FILING;
  return undefined;
}

/**
 * CCM-R1-REVIEW-EXCEPTION-FRAME (D-CCM-71's L2): whether a question asks if the organization is one
 * of a state's exceptions. True when every condition it decides is in a reviewed reading's
 * exceptions, which the evaluator addresses under `/filing_exceptions/` (cut 1008-10's D6). Their
 * words are a noun phrase, such as § 6-405(a)'s, so L2 frames them. A question with any other
 * condition, or none named, keeps N21: "one of these:" never stands over a clause.
 */
function asksAnException(question: ReviewQuestion): boolean {
  const paths = question.decides.flatMap((ref) => ref.paths);
  return (
    paths.length > 0 &&
    paths.every(
      (address) =>
        address.origin === 'representation' && address.path.startsWith('/filing_exceptions/'),
    )
  );
}

/**
 * The card a question is asked on, and N2 counts:
 * - one card per profile field it fills;
 * - one per state for an organization's own answers, the state's exceptions on a card of their own;
 * - otherwise its own card: a question in Givmo's own words (W5, no state's) and a figure that no
 *   longer stands, asked again, among them.
 */
function questionCardKey(item: ReviewItem, question: ReviewQuestion): string {
  if (question.answerParameterKey !== undefined) return `param:${question.answerParameterKey}`;
  if (isStaleRecord(question)) return `stale:${item.itemKey}`;
  const state = question.decides[0]?.jurisdictionCode ?? '';
  if (asksTheOnlineLeg(question)) return `online:${state}`;
  // The federal exemption, unread in Givmo's records, is asked in the statute's words beside the
  // state's other questions (cut 1008-86's J-A2).
  if (question.factKey === 'federal_tax_exemption_in_effect' && question.sourceWords !== undefined)
    return `ask:${state}`;
  if (question.factKey !== 'organization_answer' || question.moduleWords !== undefined)
    return `item:${item.itemKey}`;
  return `${asksAnException(question) ? 'exception' : 'ask'}:${state}`;
}

/**
 * S's record (cut 1008-125): the federal exemption read off Givmo's copy of the IRS lists while the
 * copy is past its freshness rule, which nothing the organization answers settles. The one other
 * fact served stale is a figure counted so far that no longer stands (C1, cut 1008-122), which the
 * organization answers again, so it keeps its own card and stays open.
 */
function isStaleRecord(question: ReviewQuestion): boolean {
  return question.state === 'stale' && question.factKey === 'federal_tax_exemption_in_effect';
}

/** J-C: a state's own question on the online leg of its soliciting-in-the-State prong. */
function asksTheOnlineLeg(question: ReviewQuestion): boolean {
  const paths = question.decides.flatMap((ref) => ref.paths);
  return paths.length > 0 && paths.every((address) => address.path.endsWith('/online/answer'));
}

export function buildReviewView(input: ReviewModelInput): ReviewView {
  const { items, results, orgName, personName, revision } = input;
  const names = ruleNamesOf(results);
  const resultsByKey = new Map(results.map((result) => [ruleKey(result.rule), result]));
  const title = (ref: RuleRef) => filingTitle(ref, names);
  const markOf = (itemKey: string): Mark | undefined => {
    if (input.previousItemKeys !== undefined && !input.previousItemKeys.has(itemKey)) return 'new';
    if (input.changedItemKeys?.has(itemKey) === true) return 'changed';
    return undefined;
  };
  const evaluatedOn = displayComplianceDate(revision.asOf);
  const calendarRules = new Set([...input.calendar.values()].map((entry) => entry.ruleKey));

  const factLines = (result: ReviewResult, since?: string): FactLine[] =>
    result.inputsUsed.map((reading) => {
      const words = factLabelAndValue(reading, result, names, revision.asOf);
      const correctAt = ANSWERED_AT[reading.factKey];
      const source = recordSource(reading, orgName);
      if (source !== undefined) return { key: reading.requirement, ...words, source };
      return {
        key: reading.requirement,
        ...words,
        // After the line's middle dot, so an unnamed recorder reads lowercase (D-CCM-99).
        by:
          reading.recordedByUserId !== undefined
            ? (personName(reading.recordedByUserId) ?? SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE)
            : undefined,
        on:
          reading.recordedOn !== undefined ? displayComplianceDate(reading.recordedOn) : undefined,
        ...(correctAt !== undefined ? { correctAt } : {}),
        ...(since !== undefined && reading.recordedOn !== undefined && reading.recordedOn > since
          ? { changed: true }
          : {}),
      };
    });

  // A card's basis is its own rule's conditions. A duty a rule lifts, or an exemption inside a duty,
  // carries the other rule's conditions too; those show on that rule's own card, which this card's
  // line names (N12), never here as if they were this rule's.
  const basisOf = (result: ReviewResult): Basis => ({
    conditions: result.conditions
      .filter(
        (condition) =>
          condition.path.jurisdictionCode === result.rule.jurisdictionCode &&
          condition.path.ruleId === result.rule.ruleId,
      )
      .map((condition) => ({
        words: conditionWords(condition, names, revision.asOf, result.inputsUsed),
        outcome: condition.outcome,
      })),
    evaluatedOn,
  });

  /** W4 (MD-25K's G2): what a result's line leaves open, drawn as N24's note is, beside the result. */
  const openDetailOf = (result: ReviewResult): EvidenceLine | undefined => {
    const open = result.openDetail;
    if (open === undefined) return undefined;
    return {
      lead: open.words,
      quote: `“${open.quotedSourceText}”`,
      citation: open.displayCitation,
    };
  };

  /**
   * The figures a dollar line read where two sources disagree on its year (J3): each year's, the
   * current year's first, as the facts list names them (W3 through its day, N54).
   */
  const figureLinesOf = (own: ResultCondition, result: ReviewResult): FactLine[] => {
    const years = result.inputsUsed
      .filter(
        (reading) =>
          reading.factKey === 'monetary_figure' && own.inputRefs.includes(reading.requirement),
      )
      .sort((a, b) => (b.dimensions.period ?? '').localeCompare(a.dimensions.period ?? ''))
      .map((reading) => reading.requirement);
    return factLines(result)
      .filter((fact) => years.includes(fact.key))
      .sort((a, b) => years.indexOf(a.key) - years.indexOf(b.key));
  };

  /** N24 from the served limit of the rule whose met conditions cause the no-filing (J1). */
  const evidenceOf = (result: ReviewResult | undefined): EvidenceLine | undefined => {
    const evidence = result?.requiredEvidence;
    if (result === undefined || evidence === undefined) return undefined;
    return {
      lead: W.mayAskForEvidence(
        result.rule.jurisdictionName || jurisdictionNameOf(result.rule.jurisdictionCode, names),
      ),
      quote: `“${evidence.quotedSourceText}”`,
      citation: evidence.displayCitation,
    };
  };
  const evidenceForNoFiling = (result: ReviewResult): EvidenceLine | undefined => {
    const exclusion = result.exclusion;
    if (exclusion?.kind === 'no_filing_required') return evidenceOf(result);
    if (exclusion?.kind === 'lifted_by_rule' && exclusion.liftingRule !== undefined) {
      return evidenceOf(resultsByKey.get(ruleKey(exclusion.liftingRule)));
    }
    return undefined;
  };

  // A filing on the calendar that now reads otherwise is its change card alone. A change that
  // takes no card (moved dates alone, or a reading that needs more facts) leaves its filing as it
  // reads: "Already on your calendar", or its questions.
  const changedRules = new Set(
    items
      .filter(
        (item) =>
          item.kind === 'change' &&
          changeKindOf(item) !== undefined &&
          item.jurisdictionCode !== undefined &&
          item.ruleId !== undefined,
      )
      .map((item) => `${item.jurisdictionCode}:${item.ruleId}`),
  );

  const changes: ChangeCard[] = [];
  const filings: FilingCard[] = [];
  const onCalendar: OnCalendarLine[] = [];
  const shown: ExclusionCard[] = [];
  const folded: ExclusionCard[] = [];
  const decided: DecidedCard[] = [];
  const questions = new Map<string, QuestionCard>();
  const issues = new Map<string, IssueCard>();

  for (const item of items) {
    const ref: RuleRef | undefined =
      item.jurisdictionCode !== undefined && item.ruleId !== undefined
        ? { jurisdictionCode: item.jurisdictionCode, ruleId: item.ruleId }
        : undefined;
    const result = item.result ?? (ref !== undefined ? resultsByKey.get(ruleKey(ref)) : undefined);
    const standing = standingOn(item, revision);
    const reopened = reopenedOn(item, revision);

    switch (item.kind) {
      case 'filing_work': {
        if (ref === undefined || result === undefined || changedRules.has(ruleKey(ref))) break;
        const obligationId = item.existingObligationId;
        if (obligationId !== undefined && input.calendar.has(obligationId)) {
          // Linked, never duplicated: a filing already on the calendar needs no choice here.
          onCalendar.push({
            itemKey: item.itemKey,
            title: title(ref),
            obligationId,
            ...(item.decision !== undefined && input.othersSince?.has(item.itemKey) === true
              ? { addedBy: item.decision }
              : {}),
          });
          break;
        }
        const stopped = obligationId !== undefined ? input.stopped?.get(obligationId) : undefined;
        if (
          standing !== undefined &&
          settles(standing) &&
          standsSinceStop(standing, stopped?.stoppedAt)
        ) {
          decided.push({
            itemKey: item.itemKey,
            kind: item.kind,
            ref,
            title: title(ref),
            decision: standing,
            offerAddFiling: false,
          });
          break;
        }
        const quoted =
          reopened !== undefined && reopened.decision === 'deny' ? reopened : undefined;
        const since = localDayOf(quoted?.decidedAt);
        const facts = factLines(result, since);
        const openDetail = openDetailOf(result);
        filings.push({
          itemKey: item.itemKey,
          ref,
          title: title(ref),
          ...(stopped !== undefined ? { stopped } : {}),
          date: previewCard(item.previewed, result, input.fiscalYearEnd, revision.asOf),
          facts,
          basis: basisOf(result),
          ...(openDetail !== undefined ? { openDetail } : {}),
          mark: markOf(item.itemKey),
          ...(quoted !== undefined
            ? {
                reopened: {
                  decision: quoted,
                  // The facts it rested on moved where one was recorded after it; else the rule did.
                  cause: facts.some((fact) => fact.changed === true)
                    ? ('facts' as const)
                    : ('rule' as const),
                },
              }
            : {}),
        });
        break;
      }
      case 'exclusion': {
        if (ref === undefined || result === undefined || changedRules.has(ruleKey(ref))) break;
        if (standing !== undefined && settles(standing)) {
          decided.push({
            itemKey: item.itemKey,
            kind: item.kind,
            ref,
            title: title(ref),
            decision: standing,
            // JQ2: offered until the filing is on the calendar.
            offerAddFiling: standing.decision === 'deny' && !calendarRules.has(ruleKey(ref)),
          });
          break;
        }
        const exclusion = result.exclusion;
        const lifter = exclusion?.liftingRule;
        const openDetail = openDetailOf(result);
        const card: ExclusionCard = {
          itemKey: item.itemKey,
          ref,
          title: title(ref),
          label:
            exclusion?.kind === 'no_filing_required'
              ? W.CONDITIONS_MET_NO_FILING
              : exclusion?.kind === 'lifted_by_rule' && lifter !== undefined
                ? W.meetsConditionsOf(title(lifter))
                : decidedByRecordsAlone(result)
                  ? W.RECORDS_DONT_MEET_CONDITIONS
                  : W.CONDITIONS_NOT_MET,
          ...(openDetail !== undefined ? { openDetail } : {}),
          evidence: evidenceForNoFiling(result),
          facts: factLines(result),
          basis: basisOf(result),
          mark: markOf(item.itemKey),
        };
        // A no-filing someone was asked about is never folded away (CCM-R1-REVIEW-ASK-NOTE-SEEN): the
        // person asked reads the ask, and its note, on the card.
        if (exclusion?.kind === 'conditions_not_met' && item.ask === undefined) folded.push(card);
        else shown.push(card);
        break;
      }
      case 'question': {
        const question = item.question;
        if (question === undefined) break;
        const answeredAt = question.answerParameterKey;
        const group = questionCardKey(item, question);
        const decides = question.decides
          .filter((ref) => isFiling(ref, names))
          .map((ref) => title(ref));
        const existing = questions.get(group);
        if (existing !== undefined) {
          existing.itemKeys.push(item.itemKey);
          for (const name of decides)
            if (!existing.decides.includes(name)) existing.decides.push(name);
          if (
            question.sourceWords !== undefined &&
            !existing.quoted.includes(question.sourceWords)
          ) {
            existing.quoted.push(question.sourceWords);
          }
          if (existing.standing === undefined && standing?.decision === 'defer')
            existing.standing = standing;
          existing.mark ??= markOf(item.itemKey);
          break;
        }
        const card: QuestionCard = {
          key: group,
          itemKeys: [item.itemKey],
          title: '',
          quoted: question.sourceWords !== undefined ? [question.sourceWords] : [],
          listTitle: '',
          decides,
          control: { kind: 'none' },
          mark: markOf(item.itemKey),
          ...(standing?.decision === 'defer' ? { standing } : {}),
          ...(item.sortOn !== undefined ? { sortOn: item.sortOn } : {}),
        };
        const stateName = jurisdictionNameOf(question.decides[0]?.jurisdictionCode ?? '', names);
        if (group.startsWith('stale:')) {
          card.kind = 'stale';
          card.title =
            question.factKey === 'federal_tax_exemption_in_effect'
              ? W.FEDERAL_TAX_EXEMPTION_IN_EFFECT
              : servedQuestionWords(item, resultsByKey);
          // S, in the door's line (the one line a card gives under its decides line).
          card.door = W.IRS_LISTS_OUT_OF_DATE;
          card.quoted = [];
          questions.set(group, card);
          break;
        }
        if (group.startsWith('online:')) {
          card.kind = 'online';
          card.title = W.ASKING_ONLINE;
          // N21 over the source's words, in the door's line.
          card.door = W.sourceAsks(stateName);
          if (question.sourceDisplayCitation !== undefined)
            card.citation = question.sourceDisplayCitation;
          if (question.example !== undefined) {
            card.example = {
              lead: W.anExampleStateGives(stateName),
              quote: `“${question.example.quotedSourceText}”`,
              ...(question.example.displayCitation !== undefined
                ? { citation: question.example.displayCitation }
                : {}),
            };
          }
          if (question.fee !== undefined) {
            card.fee = {
              caption: W.feeChartFromForm(
                stateName,
                question.fee.chart.page,
                question.fee.formNumber,
              ),
              chart: question.fee.chart,
            };
          }
          questions.set(group, card);
          break;
        }
        switch (question.factKey) {
          case 'located_in':
            card.title = W.isLocatedIn(
              orgName,
              jurisdictionNameOf(question.dimensions.jurisdiction ?? '', names),
            );
            // W1-a, in the door's line.
            card.door = W.recordsShowNoState(orgName);
            if (question.sourceDisplayCitation !== undefined)
              card.citation = question.sourceDisplayCitation;
            break;
          case 'federal_tax_exemption_in_effect':
            // Asked in the statute's words, it opens N21's card of the state's own questions; served
            // without them, it keeps the condition's words, as it always has.
            card.title =
              card.quoted.length > 0
                ? W.sourceAsks(stateName)
                : servedQuestionWords(item, resultsByKey);
            break;
          // The states list answers both: whichever of its questions comes first makes the card.
          case 'solicitation_activity':
          case 'solicitation_outside':
            card.title = W.whereAsks(orgName);
            if (answeredAt !== undefined) card.control = { kind: 'jurisdictions' };
            break;
          case 'fiscal_year_end':
            card.title = W.whenYearEnds(orgName);
            if (answeredAt !== undefined) card.control = { kind: 'month_day' };
            break;
          case 'engages_paid_fundraiser':
            card.title = W.engagesPaidFundraiser(orgName);
            if (answeredAt !== undefined)
              card.control = { kind: 'boolean', parameterKey: answeredAt };
            break;
          case 'solicits_nationwide_or_online':
            // Asked in the profile's own words: its served label is the question's name.
            card.title = PROFILE_LABELS.solicits_nationwide_or_online ?? question.factKey;
            if (answeredAt !== undefined)
              card.control = { kind: 'boolean', parameterKey: answeredAt };
            break;
          case 'monetary_figure': {
            // Cut 1008-20's W1 for a year not yet over on the revision's day, else N20.
            const soFar = soFarYearEnd(question.dimensions.period, revision.asOf);
            const period = fiscalPeriodWords(question.dimensions.period);
            const counted = question.dimensions.measure === 'contributions_from_the_public';
            card.title =
              counted && soFar !== undefined
                ? W.contributionsSoFarFromPublic(orgName, soFar)
                : counted && period !== undefined
                  ? W.contributionsFromPublic(orgName, period)
                  : servedQuestionWords(item, resultsByKey);
            break;
          }
          case 'organization_answer':
            // W5 (cut 1008-122): a question Givmo asks in its own words is titled in them, with no
            // state's lead and no quotation; every other one opens N21's (or L2's) card.
            card.title =
              question.moduleWords ??
              (asksAnException(question) ? W.sourceAsksIsOneOf : W.sourceAsks)(
                jurisdictionNameOf(question.decides[0]?.jurisdictionCode ?? '', names),
              );
            break;
          case 'register_record': {
            // RQ1 and RQ2 (D-CCM-112): answered by recording the filing in Filings, so no field here
            // (a "no" waits for the fact store, D-CCM-14).
            const jurisdiction = jurisdictionNameOf(question.dimensions.jurisdiction ?? '', names);
            const { record, obligation_class: filingClass } = question.dimensions;
            card.title =
              W.registerRecordQuestion(orgName, record, filingClass, jurisdiction) ??
              servedQuestionWords(item, resultsByKey);
            const door = W.registerRecordDoor(record, filingClass, jurisdiction);
            if (door !== undefined) card.door = door;
            break;
          }
          default:
            card.title = servedQuestionWords(item, resultsByKey);
        }
        questions.set(group, card);
        break;
      }
      case 'issue': {
        if (result === undefined) break;
        // CCM-R1-REVIEW-WEB-ISSUE-CARDS: each card is the item's OWN condition (its served
        // `issue_path`), never its rule's: one rule can leave two conditions unsettled (Maryland's
        // online question and its $25,000 line), and each is a card of its own, in its own words.
        // An item whose condition its result doesn't carry keeps its filing's title and no words,
        // never another condition's.
        const issuePath = item.issuePath;
        const own =
          issuePath !== undefined
            ? result.conditions.find(
                (condition) => addressKey(condition.path) === addressKey(issuePath),
              )
            : undefined;
        const reasonWords = own?.reasonWords !== undefined ? [own.reasonWords] : [];
        // Cut 1008-122 (J3): where two sources disagree, each one's words, quoted, with its citation.
        const quotes = [
          { words: own?.sourceWords, citation: own?.sourceDisplayCitation },
          { words: own?.secondSourceWords, citation: own?.secondSourceDisplayCitation },
        ].flatMap(({ words, citation }) => (words !== undefined ? [{ words, citation }] : []));
        const sourceWords = quotes.map((quote) => quote.words);
        const sourceCitations = quotes.map((quote) => quote.citation);
        // W4 (MD-25K's G2) leads the source's words it quotes, never quoting them a second time.
        const openDetailLead =
          result.openDetail !== undefined && sourceWords[0] === result.openDetail.quotedSourceText
            ? result.openDetail.words
            : undefined;
        const online = own?.kind === 'online_presence' && own.outcome === 'unknown';
        const group = `${item.issueKind ?? ''}:${online ? 'online' : (sourceWords[0] ?? item.itemKey)}`;
        const blocks = item.issueBlocks.map((ref) => title(ref));
        const existing = issues.get(group);
        if (existing !== undefined) {
          existing.itemKeys.push(item.itemKey);
          for (const name of blocks)
            if (!existing.blocks.includes(name)) existing.blocks.push(name);
          break;
        }
        issues.set(group, {
          key: group,
          itemKeys: [item.itemKey],
          // N55 for the online question; else the filing the condition is written in, by its name.
          title: online ? W.ASKING_ONLINE : ref !== undefined ? title(ref) : '',
          reasonWords,
          sourceWords,
          sourceCitations,
          ...(openDetailLead !== undefined ? { openDetailLead } : {}),
          blocks,
          facts: online
            ? factLines(result).filter((fact) => fact.key === 'solicits_nationwide_or_online')
            : own?.secondSourceWords !== undefined
              ? figureLinesOf(own, result)
              : [],
          mark: markOf(item.itemKey),
        });
        break;
      }
      case 'change': {
        const change = item.change;
        const kind = changeKindOf(item);
        if (change === undefined || kind === undefined || ref === undefined || result === undefined)
          break;
        // "Keep tracking it" stands while what it rested on is unchanged (D2); "Stop tracking it"
        // stands only while the filing stays stopped (O3's twin).
        if (
          standing !== undefined &&
          settles(standing) &&
          !undoneByRestart(kind, standing, input.calendar.has(change.obligationId))
        ) {
          decided.push({
            itemKey: item.itemKey,
            kind: item.kind,
            changeKind: kind,
            ref,
            title: title(ref),
            decision: standing,
            offerAddFiling: false,
          });
          break;
        }
        // N36's day: the version Givmo's rule took (D6), else the day the filing was flagged.
        const updatedDay =
          change.assessedRuleVersionLoadedOn ??
          input.calendar.get(change.obligationId)?.ruleChangeFlaggedOn;
        const updatedOn =
          change.changes.includes('rule_reversioned') && updatedDay !== undefined
            ? displayComplianceDate(updatedDay)
            : undefined;
        const line = kind === 'no_filing' ? noFilingLine(result, title) : undefined;
        changes.push({
          itemKey: item.itemKey,
          title: title(ref),
          kind,
          ...(line !== undefined ? { line } : {}),
          ...(updatedOn !== undefined ? { updatedOn } : {}),
          ...(kind === 'no_filing' ? { evidence: evidenceForNoFiling(result) } : {}),
          obligationId: change.obligationId,
          mark: markOf(item.itemKey),
        });
        break;
      }
      default:
        break;
    }
  }

  for (const card of questions.values()) {
    const first = card.quoted[0];
    card.listTitle =
      first !== undefined
        ? `${card.title.replace(/:$/, '')} “${shortWords(first)}”${card.quoted.length > 1 ? ` and ${card.quoted.length - 1} more` : ''}`
        : card.title;
  }

  // Not supported: the count, every rule's name, and those in places the organization told Givmo.
  const unsupported = results.filter((result) => result.result === 'not_supported');
  const titled = unsupported.filter((result) => result.rule.displayTitle !== undefined);
  const counts = revision.resultCounts;
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0) || results.length;
  const notSupportedCount = counts.not_supported ?? unsupported.length;
  const evaluatedRuleCount = Math.max(0, total - notSupportedCount);
  return {
    changes,
    filings,
    onCalendar,
    questions: [...questions.values()].sort(byUpcomingWork),
    issues: [...issues.values()],
    noFiling: { shown, folded },
    decided,
    notSupported: {
      count: notSupportedCount,
      titles: titled.map((result) => title(result.rule)),
      inPlacesTold: titled
        .filter((result) => input.toldJurisdictions?.has(result.rule.jurisdictionCode) === true)
        .map((result) => title(result.rule)),
    },
    evaluatedRuleCount,
    anythingEvaluates: evaluatedRuleCount > 0,
  };
}

/**
 * F2 (review-182): a rule whose conditions aren't met where every condition that decides it reads
 * only Givmo's own records (the IRS address or lists), never a fact the organization recorded.
 */
export function decidedByRecordsAlone(result: ReviewResult): boolean {
  const exclusion = result.exclusion;
  if (exclusion?.kind !== 'conditions_not_met' || exclusion.paths.length === 0) return false;
  const readings = new Map(result.inputsUsed.map((reading) => [reading.requirement, reading]));
  return exclusion.paths.every((path) => {
    const condition = result.conditions.find(
      (candidate) => addressKey(candidate.path) === addressKey(path),
    );
    return (
      condition !== undefined &&
      condition.inputRefs.length > 0 &&
      condition.inputRefs.every((ref) => readings.get(ref)?.sourceKind === 'irs_record')
    );
  });
}

/**
 * The questions in the backend's order, prioritized by upcoming work (plan §1.7): those deciding a
 * dated filing first, the soonest first; then, and between equals, the one deciding the most filings
 * (frame 92's undated questions). The sort is stable, so cards still equal keep the items' order.
 */
function byUpcomingWork(a: QuestionCard, b: QuestionCard): number {
  // A record Givmo can't read now asks the organization nothing: after every question.
  if ((a.kind === 'stale') !== (b.kind === 'stale')) return a.kind === 'stale' ? 1 : -1;
  if ((a.sortOn === undefined) !== (b.sortOn === undefined)) return a.sortOn === undefined ? 1 : -1;
  if (a.sortOn !== undefined && b.sortOn !== undefined && a.sortOn !== b.sortOn) {
    return a.sortOn < b.sortOn ? -1 : 1;
  }
  return b.decides.length - a.decides.length;
}

/**
 * A question no approved line words: the served words of the condition its answer decides, found
 * by its whole address, as an issue card finds its own. One result carries the conditions of other
 * rules beside its own (a duty's lifters), and two rules' conditions can share a path: Maryland's
 * registration's § 6-401(a)(2) and its religious class's § 6-102(c)(1)(ii)2.B are both
 * `/rule/conditions/organization_answer/1` (CCM-R1-ASSESS-RULES-MDREG-B).
 */
function servedQuestionWords(
  item: ReviewItem,
  resultsByKey: ReadonlyMap<string, ReviewResult>,
): string {
  for (const decides of item.question?.decides ?? []) {
    const result = resultsByKey.get(ruleKey(decides));
    const path: ConditionAddress | undefined = decides.paths[0];
    const leaf = result?.conditions.find(
      (condition) => path !== undefined && addressKey(condition.path) === addressKey(path),
    );
    if (leaf !== undefined) return leaf.words;
  }
  return item.requirement ?? '';
}

/** A quotation's first words, for the decisions card's list ("is a religious organization, a parent…"). */
function shortWords(words: string): string {
  return words.length > 44 ? `${words.slice(0, 44).replace(/\s+\S*$/, '')}…` : words;
}

// ── The open count (N2, N31) ───────────────────────────────────────────────────────────────────

/** The items that still want something from the organization, as the person sees them: cards. */
export function openCount(view: ReviewView): number {
  return (
    view.changes.length +
    view.filings.length +
    view.questions.filter((card) => card.kind !== 'stale').length +
    view.issues.length +
    view.noFiling.shown.length
  );
}

/**
 * N2's count words for the entry card: "4 questions" (D-CCM-51's N2) or, with filings, "1 filing and 4
 * questions" (the design stop's draft of N2), and U28 (B)'s changes, "1 filing and 2 changes to
 * filings on your calendar"; undefined with nothing open. Open is what the review page leaves open:
 * - a filing that isn't on the calendar (`onCalendar`, its confirmed filings) and that no decision
 *   on unchanged evidence settles; a filing stopped since its decision counts (O3), `stoppedAt`
 *   holding each stopped filing's time, by obligation id;
 * - every question, "I don't know" included (frame 99's "still open");
 * - every change to a filing on the calendar the page cards, until a decision on unchanged
 *   evidence settles it: "Later" leaves it counted, as it leaves a filing, and so does a "Stop
 *   tracking it" the filing's restart undid (O3's twin).
 * Three kinds join "a, b and c", the changes last (the longest phrase).
 */
export function foundWords(
  items: readonly ReviewItem[],
  onCalendar: ReadonlySet<string>,
  stoppedAt: ReadonlyMap<string, string> = new Map(),
): { words: string; total: number } | undefined {
  const filings = items.filter(
    (item) =>
      item.kind === 'filing_work' &&
      !(item.existingObligationId !== undefined && onCalendar.has(item.existingObligationId)) &&
      !(
        item.decision !== undefined &&
        item.decision.evidenceUnchanged &&
        settles(item.decision) &&
        standsSinceStop(
          item.decision,
          item.existingObligationId !== undefined
            ? stoppedAt.get(item.existingObligationId)
            : undefined,
        )
      ),
  ).length;
  const questions = new Set(
    items
      .filter(
        (item) =>
          item.kind === 'question' &&
          !(item.question !== undefined && isStaleRecord(item.question)),
      )
      .map((item) =>
        item.question !== undefined ? questionCardKey(item, item.question) : item.itemKey,
      ),
  ).size;
  const changes = items.filter(
    (item) =>
      item.kind === 'change' &&
      changeKindOf(item) !== undefined &&
      item.jurisdictionCode !== undefined &&
      item.ruleId !== undefined &&
      !(
        item.decision !== undefined &&
        item.decision.evidenceUnchanged &&
        settles(item.decision) &&
        !undoneByRestart(
          changeKindOf(item),
          item.decision,
          item.change !== undefined && onCalendar.has(item.change.obligationId),
        )
      ),
  ).length;
  const part = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;
  const parts = [
    ...(filings > 0 ? [part(filings, 'filing', 'filings')] : []),
    ...(questions > 0 ? [part(questions, 'question', 'questions')] : []),
    ...(changes > 0 ? [W.changesToFilings(changes)] : []),
  ];
  if (parts.length === 0) return undefined;
  return { words: andList(parts), total: filings + questions + changes };
}

/**
 * U23 (D-CCM-60): each item's title as the review's own cards title it, for the revision given: a
 * question by its list title, as the decisions card names it. The review titles an ask on an item
 * it doesn't show now from the revision the ask was made in.
 */
export function itemTitlesOf(input: ReviewModelInput): Map<string, string> {
  const view = buildReviewView(input);
  const titles = new Map<string, string>();
  for (const card of [
    ...view.changes,
    ...view.filings,
    ...view.onCalendar,
    ...view.noFiling.shown,
    ...view.noFiling.folded,
    ...view.decided,
  ]) {
    titles.set(card.itemKey, card.title);
  }
  for (const card of view.questions)
    for (const key of card.itemKeys) titles.set(key, card.listTitle);
  for (const card of view.issues) for (const key of card.itemKeys) titles.set(key, card.title);
  // An item of a rule that forms no card of its own there (a filing beside its rule's change card)
  // takes its rule's title, as every rule's card does.
  const names = ruleNamesOf(input.results);
  for (const item of input.items) {
    if (
      titles.has(item.itemKey) ||
      item.jurisdictionCode === undefined ||
      item.ruleId === undefined
    )
      continue;
    titles.set(
      item.itemKey,
      filingTitle({ jurisdictionCode: item.jurisdictionCode, ruleId: item.ruleId }, names),
    );
  }
  return titles;
}

// ── Choices, and the act ───────────────────────────────────────────────────────────────────────

export type ChoiceValue =
  | 'add'
  | 'not_filing'
  | 'agree'
  | 'we_file'
  | 'dont_know'
  | 'later'
  // A filing on the calendar that reads otherwise (D-CCM-51's choice 2; cut 1008-11's D1).
  | 'use_updated'
  | 'change_later'
  | 'keep_tracking'
  | 'stop_tracking';

export interface Choice {
  value: ChoiceValue;
  /** "Why? (optional)", on "Not filing". */
  reason?: string;
}

/**
 * What each choice records (D1: a filing or a no-filing takes accept, deny or defer; the rest defer).
 * A change takes Givmo's new reading with `accept`: "Use the updated rule" moves the filing to the
 * updated rule, "Stop tracking it" stops it. "Keep tracking it" is `deny`, the calendar as it is,
 * and "Later" is `defer` (cut 1008-11's D1).
 */
export function wireDecision(value: ChoiceValue): 'accept' | 'deny' | 'defer' {
  switch (value) {
    case 'add':
    case 'agree':
    case 'use_updated':
    case 'stop_tracking':
      return 'accept';
    case 'not_filing':
    case 'we_file':
    case 'keep_tracking':
      return 'deny';
    case 'dont_know':
    case 'later':
    case 'change_later':
      return 'defer';
  }
}

/** Each choice's label, in the order the decisions card lists them. */
export const CHOICE_LABELS: readonly { value: ChoiceValue; label: string }[] = [
  { value: 'use_updated', label: W.USE_UPDATED_RULE },
  { value: 'stop_tracking', label: W.STOP_TRACKING },
  { value: 'keep_tracking', label: W.KEEP_TRACKING },
  { value: 'change_later', label: W.LATER },
  { value: 'add', label: W.ADD_TO_CALENDAR },
  { value: 'not_filing', label: W.NOT_FILING },
  { value: 'agree', label: W.THATS_RIGHT },
  { value: 'we_file', label: W.WE_FILE_IT },
  { value: 'dont_know', label: W.I_DONT_KNOW },
  { value: 'later', label: W.DECIDE_LATER },
];

export interface Selection {
  decisions: { item_key: string; decision: 'accept' | 'deny' | 'defer'; reason?: string }[];
  /** The cards chosen, by choice, for the decisions card. */
  chosen: { key: string; title: string; value: ChoiceValue }[];
}

/** Every choice made on the cards this view shows, as the act carries them (one per item key). */
export function selectionOf(choices: ReadonlyMap<string, Choice>, view: ReviewView): Selection {
  const cards = new Map<string, { title: string; itemKeys: string[] }>();
  for (const card of view.changes)
    cards.set(card.itemKey, { title: card.title, itemKeys: [card.itemKey] });
  for (const card of view.filings)
    cards.set(card.itemKey, { title: card.title, itemKeys: [card.itemKey] });
  for (const card of [...view.noFiling.shown, ...view.noFiling.folded]) {
    cards.set(card.itemKey, { title: card.title, itemKeys: [card.itemKey] });
  }
  for (const card of view.questions)
    cards.set(card.key, { title: card.listTitle, itemKeys: card.itemKeys });
  for (const card of view.issues)
    cards.set(card.key, { title: card.title, itemKeys: card.itemKeys });
  const decisions: Selection['decisions'] = [];
  const chosen: Selection['chosen'] = [];
  for (const [key, choice] of choices) {
    const card = cards.get(key);
    if (card === undefined) continue;
    const decision = wireDecision(choice.value);
    const reason = choice.reason?.trim();
    for (const itemKey of card.itemKeys) {
      decisions.push({
        item_key: itemKey,
        decision,
        // An acceptance carries no reason (the backend refuses one).
        ...(decision !== 'accept' && reason !== undefined && reason.length > 0 ? { reason } : {}),
      });
    }
    chosen.push({ key, title: card.title, value: choice.value });
  }
  return { decisions, chosen };
}

/** The choice a decision recorded, in its card's words, for the decided list. */
export function decidedChoiceLabel(
  kind: string,
  decision: string,
  changeKind?: ChangeKind,
): string {
  if (kind === 'change')
    return decision === 'deny'
      ? W.KEEP_TRACKING
      : decision === 'accept'
        ? changeKind === 'no_filing'
          ? W.STOP_TRACKING
          : W.USE_UPDATED_RULE
        : W.LATER;
  if (kind === 'exclusion')
    return decision === 'deny'
      ? W.WE_FILE_IT
      : decision === 'accept'
        ? W.THATS_RIGHT
        : W.DECIDE_LATER;
  if (kind === 'filing_work')
    return decision === 'deny'
      ? W.NOT_FILING
      : decision === 'accept'
        ? W.ADD_TO_CALENDAR
        : W.DECIDE_LATER;
  if (kind === 'question') return W.I_DONT_KNOW;
  return W.DECIDE_LATER;
}

// ── Re-presentation (N33) ──────────────────────────────────────────────────────────────────────

/**
 * What an item showed, comparable across revisions: its kind, its result and how it came out, the
 * dates it would take and every fact it read. An item whose signature moved is "Changed", and a
 * choice made on it before is let go (D9).
 */
export function itemSignature(
  item: ReviewItem,
  results: ReadonlyMap<string, ReviewResult>,
): string {
  const ref =
    item.jurisdictionCode !== undefined && item.ruleId !== undefined
      ? `${item.jurisdictionCode}:${item.ruleId}`
      : '';
  const result = item.result ?? results.get(ref);
  return JSON.stringify({
    kind: item.kind,
    result: result?.result,
    exclusion: result?.exclusion?.kind,
    lifting:
      result?.exclusion?.liftingRule !== undefined
        ? ruleKey(result.exclusion.liftingRule)
        : undefined,
    dates: item.previewed.map((entry) => [
      entry.dueOn ?? null,
      entry.extension?.extendedDueOn ?? null,
    ]),
    facts: result?.inputsUsed.map((reading) => [reading.requirement, reading.value]) ?? [],
    question:
      item.question !== undefined
        ? [item.question.state, item.question.decides.map((ref) => ruleKey(ref))]
        : null,
  });
}

/** The stale refusal's reasons, in N32's words (another person's save is said once). */
export function staleReasonLines(
  reasons: readonly string[],
  savedBy?: { name: string; time: string },
): string[] {
  const lines: string[] = [];
  const reviewed = reasons.includes('reviewed') && savedBy !== undefined;
  if (reviewed) lines.push(W.savedMeanwhile(savedBy.name, savedBy.time));
  if (reasons.includes('rules_changed')) lines.push(W.REASON_RULES);
  if (reasons.includes('facts_changed') || reasons.includes('facts_not_adopted'))
    lines.push(W.REASON_FACTS);
  if (reasons.includes('calendar_changed') && !reviewed) lines.push(W.REASON_CALENDAR);
  if (reasons.includes('superseded') && !reviewed) lines.push(W.REASON_SUPERSEDED);
  return lines;
}
